/**
 * Wire shapes: the JSON the backends emit. Field names are verbatim from
 * vms_database's Go structs and metadata's openapi.yaml. The answer types come from their
 * schemas; the request types are interfaces.
 */

import { z } from 'zod';

import { keyBy, listOf } from '@/api/listOf';

import {
    ReidentifyIdentity as ReidentifyIdentitySchema, ReidentifyResult as ReidentifyResultSchema,
} from './generated/metadata.zod';
import {
    Enrollment as GeneratedEnrollmentSchema,
    EnrollmentDetail as GeneratedEnrollmentDetailSchema,
    EnrollmentExemplar as EnrollmentExemplarSchema,
    EnrollmentSettingsResponse as GeneratedEnrollmentSettingsSchema,
    EnrollResponse as GeneratedEnrollResponseSchema,
} from './generated/vms_database.zod';

/** A ReID class: the two the UI has a form for, or one a newer model added. */
export type EnrollmentClass =
    'object.person' | 'object.vehicle' | (string & NonNullable<unknown>);

const EnrollmentClassSchema = z.custom<EnrollmentClass>(
    (value: unknown): boolean => typeof value === 'string',
);

/** One similarity threshold per ReID class; the server always fills the two known ones. */
const ClassThresholdsSchema = z.object({
    'object.person': z.number(),
    'object.vehicle': z.number(),
}).catchall(z.number());
export type ClassThresholds = z.infer<typeof ClassThresholdsSchema>;

export const EnrollmentSchema = GeneratedEnrollmentSchema.extend({
    categoryClass: EnrollmentClassSchema,
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

/** The organization's known objects as one fetch leaves them. */
export interface EnrollmentsSnapshot {
    enrollments: Enrollment[];
    isLoading: boolean;
    error: Error | null;
}

export { EnrollmentExemplarSchema };
export type EnrollmentExemplar = z.infer<typeof EnrollmentExemplarSchema>;

/** GET /enrollments/{id}: the identity with its exemplars. */
export const EnrollmentDetailSchema = GeneratedEnrollmentDetailSchema.extend({
    categoryClass: EnrollmentClassSchema,
    exemplars: listOf(EnrollmentExemplarSchema, 'exemplars', { getKey: keyBy('exemplarId') }),
});
export type EnrollmentDetail = z.infer<typeof EnrollmentDetailSchema>;

/** One identity with its embeddings, as one fetch leaves it. */
export interface EnrollmentDetailSnapshot {
    enrollment: EnrollmentDetail | null;
    isLoading: boolean;
    error: Error | null;
}

/** One identity's share of a re-identification sweep. */
export type ReidentifyIdentity = z.infer<typeof ReidentifyIdentitySchema>;

/** What a sweep over the stored objects reports: metadata's answer, generated. */
export const ReidentifyResponseSchema = ReidentifyResultSchema.extend({
    identities: listOf(ReidentifyIdentitySchema, 'identities', {
        getKey: keyBy('enrollmentId'),
    }),
});
export type ReidentifyResponse = z.infer<typeof ReidentifyResponseSchema>;

/** One object to enrol: its ReID vector, class, best-shot image and source event. */
export interface EnrollExemplar {
    eventId: string;
    deviceId: string;
    categoryClass: EnrollmentClass;
    embedding: number[];
    image: { mimeType: string; data: string };
}

export interface EnrollRequest {
    name: string;
    /** Enrols the exemplars as "NOT" counter-examples of an existing name. */
    negative?: boolean;
    exemplars: EnrollExemplar[];
}

/**
 * What one enroll POST answers with: the identity it landed in, and the fate
 * of every exemplar sent. `exemplarCount` is the REQUEST POLARITY's gallery
 * size afterwards, not the identity's combined total.
 */
export const EnrollResponseSchema = GeneratedEnrollResponseSchema.extend({
    categoryClass: EnrollmentClassSchema,
});
export type EnrollResponse = z.infer<typeof EnrollResponseSchema>;

/** Per-org matching thresholds; one similarity threshold per ReID class. */
export const EnrollmentSettingsSchema = GeneratedEnrollmentSettingsSchema.extend({
    byClass: ClassThresholdsSchema,
});
export type EnrollmentSettings = z.infer<typeof EnrollmentSettingsSchema>;

/** The organization's thresholds, as one fetch or one write leaves them. */
export interface EnrollmentSettingsSnapshot {
    settings: EnrollmentSettings | null;
    isLoading: boolean;
    error: Error | null;
}

/** What an enrollment write changed, by name. */
export enum EnrollmentChangeKind {
    Enrolled = 'enrolled',
    Edited = 'edited',
    Deleted = 'deleted',
    Reidentified = 'reidentified',
    ThresholdsSet = 'thresholdsSet',
}

/**
 * `Enrolled` carries the events it put on the identity, so a list can show the verdict before
 * the server's rows do. `Deleted` is apart from `Edited`: rows that name the identity now name
 * nothing.
 */
export type EnrollmentChange =
    {
        kind: EnrollmentChangeKind.Enrolled;
        enrollmentId: string;
        isNegative: boolean;
        eventIds: string[];
    } |
    { kind: EnrollmentChangeKind.Edited | EnrollmentChangeKind.Deleted; enrollmentId: string } |
    { kind: EnrollmentChangeKind.Reidentified | EnrollmentChangeKind.ThresholdsSet };

/** The last change; `seq` tells a reader which changes came after its mount. */
export interface EnrollmentChangeNotice {
    seq: number;
    change: EnrollmentChange;
}

/** What a save sends: `topK` is not exposed, and the server fills it. */
export interface EnrollmentSettingsDraft {
    negativeGap: number;
    byClass: ClassThresholds;
}
